import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../../store/auth-context';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { PreviewShell } from '../../preview/PreviewShell';
import {
    PreviewFooter,
    PreviewRunningHeader,
    PreviewSection,
    type PreviewHeaderConfig,
    type PreviewMode,
} from '@nordinsight/implant-core/ui';
import type { DocumentSchema } from '../../documents/types';
import type { PolicyDocumentMeta } from './PolicyDocumentView';
import {
    buildPolicyHeaderConfig,
    buildPolicyPreviewSections,
} from './policyPreviewData';
import { buildPolicyPlainText } from './policyPlainText';

interface PolicyPreviewDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    schema: DocumentSchema;
    data: Record<string, any>;
    meta: PolicyDocumentMeta;
}

export function PolicyPreviewDialog({
    open,
    onOpenChange,
    schema,
    data,
    meta,
}: PolicyPreviewDialogProps) {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const [mode, setMode] = useState<PreviewMode>('print');

    const headerConfig = useMemo<PreviewHeaderConfig>(
        () => buildPolicyHeaderConfig(meta, t),
        [meta, t]
    );

    const printFooter = useMemo(() => {
        const facilityName = ctx.getContext()?.department?.name;
        const generatedAt = new Date().toLocaleDateString();
        return (
            <PreviewFooter
                left={['NordInsight', facilityName].filter(Boolean).join(' · ')}
                right={t('preview.printed', { date: generatedAt })}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, open, t]);

    const sections = useMemo(
        () => buildPolicyPreviewSections({ schema, data, t }),
        [schema, data, t]
    );

    const plainText = useMemo(
        () => buildPolicyPlainText({ schema, data, meta, t }),
        [schema, data, meta, t]
    );

    const generateCopyText = useCallback(() => plainText, [plainText]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
                <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6 pr-14">
                    <DialogTitle>
                        {mode === 'print'
                            ? meta.title ||
                              t('departmentPolicies.preview.exportPolicy')
                            : t('preview.shell.copyOverview')}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                        {mode === 'print'
                            ? t('preview.common.formatForPrint')
                            : t('preview.common.plainTextSummaryShort')}
                    </p>
                </DialogHeader>
                <PreviewShell
                    className="min-h-0 flex-1"
                    mode={mode}
                    onModeChange={setMode}
                    header={headerConfig}
                    hasContent={sections.length > 0}
                    printHeader={
                        <PreviewRunningHeader
                            label={
                                meta.title ||
                                t('departmentPolicies.preview.policyFallback')
                            }
                        />
                    }
                    printHeaderSkipFirstPage
                    printFooter={printFooter}
                    generateCopyText={generateCopyText}
                    showPrint={mode === 'print'}
                    showCopy={mode === 'copy'}
                    onClose={() => onOpenChange(false)}
                    previewSource="policy"
                >
                    {mode === 'copy' ? (
                        <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed p-4">
                            {plainText}
                        </pre>
                    ) : (
                        sections.map((section) => (
                            <PreviewSection key={section.key} data={section} />
                        ))
                    )}
                </PreviewShell>
            </DialogContent>
        </Dialog>
    );
}
